/*
 *
 * Login
 *
 */

import React, { useState, useEffect, useRef, useCallback } from "react"
import { unstable_batchedUpdates } from "react-dom"
import { useDispatch } from "react-redux"
import { push } from "connected-react-router"
import { Helmet } from "react-helmet"
import { Input, Button, Divider, notification } from "antd"
import { Icon } from "components"
import AuthLayout from "../AuthLayout"
import Strings from "utils/strings"
import { setLoader, setUser, setToken } from "store/actions"
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth"
import { auth, googleProvider } from "firebase/firebase"
import { isBackofficeAllowedRole } from "utils/roles"
import "./styles.scss"

type Step = "email" | "password"

const GoogleIcon = () => (
	<svg viewBox="0 0 24 24" aria-hidden="true">
		<path
			d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
			fill="#4285F4"
		/>
		<path
			d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
			fill="#34A853"
		/>
		<path
			d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
			fill="#FBBC05"
		/>
		<path
			d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
			fill="#EA4335"
		/>
	</svg>
)

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

function Login() {
	const dispatch = useDispatch()

	const [step, setStep] = useState<Step>("email")
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [emailError, setEmailError] = useState("")
	const [passwordError, setPasswordError] = useState("")
	const [passwordLoading, setPasswordLoading] = useState(false)
	const [googleLoading, setGoogleLoading] = useState(false)

	const passwordRef = useRef<any>(null)

	useEffect(() => {
		if (step === "password") {
			const t = setTimeout(() => passwordRef.current?.focus(), 50)
			return () => clearTimeout(t)
		}
	}, [step])

	const showLoginFailed = useCallback(() => {
		notification.error({
			message: Strings.authentication.signInFailed,
			description: Strings.serverErrors.wentWrong,
			placement: "bottomRight",
			duration: 5,
		})
	}, [])

	const showNotAuthorized = useCallback(() => {
		notification.error({
			key: "auth-not-authorized",
			message: Strings.authentication.notAuthorized,
			description: Strings.authentication.notAuthorizedDescription,
			placement: "bottomRight",
			duration: 5,
		})
	}, [])

	const onAuthSuccess = useCallback(async () => {
		try {
			const firebaseUser = auth.currentUser!
			const tokenResult = await firebaseUser.getIdTokenResult()
			const role = tokenResult.claims?.role as string | undefined

			if (!role || !isBackofficeAllowedRole(role)) {
				await auth.signOut()
				showNotAuthorized()
				return
			}

			unstable_batchedUpdates(() => {
				dispatch(setToken(tokenResult.token))
				dispatch(setUser({ ...tokenResult.claims }))
				dispatch(push("/auth-test"))
			})
		} catch {
			showLoginFailed()
		}
	}, [dispatch, showLoginFailed, showNotAuthorized])

	const handleEmailNext = useCallback(() => {
		if (!email) {
			setEmailError(Strings.errors.fillEmail)
			return
		}
		if (!isValidEmail(email)) {
			setEmailError(Strings.errors.emailIsInvalid)
			return
		}
		setEmailError("")
		setStep("password")
	}, [email])

	const handlePasswordSignIn = useCallback(async () => {
		if (!password) {
			setPasswordError(Strings.errors.fillPassword)
			return
		}

		setPasswordError("")
		setPasswordLoading(true)
		dispatch(setLoader(true))

		try {
			await signInWithEmailAndPassword(auth, email, password)
			await onAuthSuccess()
		} catch {
			setPasswordError(Strings.authentication.signInFailedTryAgain)
		}

		setPasswordLoading(false)
		dispatch(setLoader(false))
	}, [dispatch, email, password, onAuthSuccess])

	const handleGoogleSignIn = useCallback(async () => {
		setGoogleLoading(true)
		dispatch(setLoader(true))

		try {
			await signInWithPopup(auth, googleProvider)
			await onAuthSuccess()
		} catch (err) {
			const code = (err as any)?.code
			if (
				code !== "auth/popup-closed-by-user" &&
				code !== "auth/cancelled-popup-request"
			) {
				showLoginFailed()
			}
		}

		setGoogleLoading(false)
		dispatch(setLoader(false))
	}, [dispatch, onAuthSuccess, showLoginFailed])

	const handleBack = useCallback(() => {
		setStep("email")
		setPassword("")
		setPasswordError("")
	}, [])

	const goToRecoverPassword = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			dispatch(push("/recover-password"))
		},
		[dispatch]
	)

	const brandTitle: Record<Step, string> = {
		email: Strings.authentication.welcomeBack,
		password: Strings.authentication.enterPassword,
	}

	const brandSubtitle: Record<Step, string> = {
		email: Strings.authentication.signInSubtitle,
		password: Strings.authentication.signingInAs,
	}

	return (
		<>
			<Helmet>
				<title>{Strings.authentication.login}</title>
				<meta name="description" content="Description of Login" />
			</Helmet>

			<AuthLayout title={brandTitle[step]} subtitle={brandSubtitle[step]}>
				<div className="LoginContent">
					{step === "email" && (
						<>
							<Button
								className="GoogleButton"
								loading={googleLoading}
								onClick={handleGoogleSignIn}
							>
								{!googleLoading && <GoogleIcon />}
								{Strings.authentication.continueWithGoogle}
							</Button>

							<Divider plain className="LoginDivider">
								{Strings.generic.or}
							</Divider>

							<div className={`LoginField${emailError ? " __error" : ""}`}>
								<Input
									value={email}
									placeholder={Strings.authentication.emailPlaceholder}
									prefix={<Icon name="user2 m10r" />}
									autoFocus
									autoComplete="email"
									onChange={(e) => {
										setEmail(e.target.value)
										if (emailError) setEmailError("")
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter") handleEmailNext()
									}}
								/>
								{emailError && (
									<span className="LoginFieldError">{emailError}</span>
								)}
							</div>

							<Button
								type="primary"
								className="LoginButton"
								onClick={handleEmailNext}
							>
								{Strings.generic.next}
							</Button>
						</>
					)}

					{step === "password" && (
						<>
							<div className="LoginEmailBadge">
								<Icon name="user2" />
								<span>{email}</span>
								<button className="LoginEmailBadgeChange" onClick={handleBack}>
									{Strings.generic.change}
								</button>
							</div>

							<div className={`LoginField${passwordError ? " __error" : ""}`}>
								<Input.Password
									ref={passwordRef}
									value={password}
									placeholder={Strings.authentication.password}
									prefix={<Icon name="lock m10r" />}
									visibilityToggle
									autoComplete="current-password"
									onChange={(e) => {
										setPassword(e.target.value)
										if (passwordError) setPasswordError("")
									}}
									onKeyDown={(e) => {
										if (e.key === "Enter") handlePasswordSignIn()
									}}
								/>
								{passwordError && (
									<span className="LoginFieldError">{passwordError}</span>
								)}
							</div>

							<a
								href="/recover-password"
								onClick={goToRecoverPassword}
								className="LoginForgotPassword"
							>
								{Strings.authentication.forgotPassword}
							</a>

							<Button
								type="primary"
								className="LoginButton"
								loading={passwordLoading}
								onClick={handlePasswordSignIn}
							>
								{Strings.authentication.login}
							</Button>
						</>
					)}
				</div>
			</AuthLayout>
		</>
	)
}

export default Login
